import { AdminIntroductions } from '@platform/client';
import { PillBadge, PillBadgeColors } from '@platform/components';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { AdminDiscoveryStatusEnum, AdminIntroduction } from 'lib/client/src/platformapi.schemas';
import { useMemo, useState, useContext } from 'react';
import { AdminTable, AdminTableContext, DateCellFromString } from '../../AdminTable';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';
import {
  ProUserProfileLink,
  OrganizationProfileLink,
  ProjectLink,
} from '../../components/ProfileLinks';
import { CreateIntroductionModal } from './CreateIntroductionModal';

const columnHelper = createColumnHelper<AdminIntroduction>();

export const IntroductionsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Introductions'}>
      <IntroductionsAdminContent />
    </AdminListPageWrapper>
  );
};

const IntroductionsAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { data, isError, isLoading, isSuccess } = AdminIntroductions.useGetIntroductions(
    { ...adminQueryOptionsV2 },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const adminIntroductions = data?.introductions || [];
  const adminIntroductionsCount = data?.totalCount || 0;

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('introductionId', {
          cell: (introductionId) => introductionId.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>ID</span>,
          maxSize: 10,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('sentByUserType', {
          cell: (sentByUserType) => <div>{sentByUserType.getValue()}</div>,
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Sent By</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('professionalName', {
          id: 'professionalName',
          cell: ({ row }) => (
            <ProUserProfileLink
              userId={row.original.professionalId}
              name={row.original.professionalName}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Pro Name</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('representativeName', {
          cell: (repName) => repName.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Rep Name</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('organizationName', {
          cell: ({ row }) => (
            <OrganizationProfileLink
              organizationId={row.original.organizationId}
              organizationName={row.original.organizationName}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Org Name</span>,
          size: 125,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('projectName', {
          cell: ({ row }) => (
            <ProjectLink
              projectId={row.original.projectId}
              projectName={row.original.projectName}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Project Name</span>,
          size: 150,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('outcome', {
          cell: ({ cell }) => {
            return <IntroOutcomeBadge outcome={cell.getValue()} />;
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Intro Outcome</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('discoveryStatus', {
          cell: (discoveryStatus) => {
            const value = discoveryStatus.getValue();
            if (value) {
              return <DiscoveryStatusBadge discoveryStatus={value} />;
            }
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Status</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('createdAt', {
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Created At</span>,
          cell: (createdAt) => <DateCellFromString value={createdAt.getValue()} />,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'date-select',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('updatedAt', {
          cell: (updatedAt) => <DateCellFromString value={updatedAt.getValue()} />,
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Updated At</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'date-select',
          },
          enableSorting: true,
        }),
        columnHelper.display({
          id: 'Actions',
          header: 'Actions',
          cell: ({ row }) => {
            return (
              <AdminActionsContainer
                actions={[{ text: 'Edit', type: 'link', href: `/${row.original.introductionId}` }]}
              />
            );
          },
          size: 25,
        }),
      ] as ColumnDef<AdminIntroduction>[],
    [],
  );

  return (
    <>
      <AdminTable
        columns={columns}
        data={adminIntroductions}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={adminIntroductionsCount}
        enableFiltering={true}
        tableActions={[
          {
            text: 'Create introduction',
            callback: () => setCreateModalOpen(true),
          },
        ]}
      />
      {createModalOpen && <CreateIntroductionModal onClose={() => setCreateModalOpen(false)} />}
    </>
  );
};

const IntroOutcomeBadge = ({ outcome }: { outcome: string }) => {
  let badgeColor: PillBadgeColors = 'error';
  if (outcome === 'accepted') {
    badgeColor = 'success-500';
  } else if (outcome === 'pending') {
    badgeColor = 'gray';
  }
  return (
    <div className='mx-auto'>
      <PillBadge variant='plain' size='md' color={badgeColor}>
        {outcome}
      </PillBadge>
    </div>
  );
};

const DiscoveryStatusBadge = ({
  discoveryStatus,
}: {
  discoveryStatus: AdminDiscoveryStatusEnum;
}) => {
  let badgeColor: PillBadgeColors = 'gray';

  if (discoveryStatus === AdminDiscoveryStatusEnum.matched) {
    badgeColor = 'success-500';
  } else if (discoveryStatus.includes('declined') || discoveryStatus.includes('unresponsive')) {
    badgeColor = 'error';
  }

  return (
    <div className='mx-auto'>
      <PillBadge variant='plain' size='md' color={badgeColor}>
        {discoveryStatus}
      </PillBadge>
    </div>
  );
};
